import network from '@/services/network';
import { ErrorResponse } from '@/utils';

/**
 * Vertriebsservice — Terminbuchung (Kurswechsel Alex 10.08.2026, Themen ab
 * Welle 2 / Entscheid Marvin 25.08.2026).
 *
 * Der Weg erzeugt einen Google-Kalendereintrag und eine Zähl-Zeile — kein
 * BW-Meeting, kein Kunde, kein Case. Deshalb gibt es hier auch keine
 * Meeting-Id in der Antwort, sondern eine Google-Event-Id.
 *
 * 🔥 `bereich` IST AN JEDEM ENDPUNKT PFLICHT und hat bewusst KEINEN Default.
 * Er entscheidet, welcher Expertenkreis gefragt wird (`ausarbeitung` → die
 * drei Coaches, `technik` → Ünal + Christian Schneider). Ein Default lieferte
 * die Zeiten des falschen Kreises — und das fällt niemandem auf, weil ein
 * Kalender mit Zeiten immer plausibel aussieht.
 */
export type VertriebsserviceBereich = 'ausarbeitung' | 'technik';

/**
 * Ein buchbares Thema. Ersetzt die sechs getrennten Buchungslinks aus dem
 * Handover §4: intern bucht der Berater angemeldet, das Thema ist deshalb ein
 * Feld am Datensatz statt einer Eigenschaft des Links.
 */
export interface VertriebsserviceThema {
  id: number;
  /** Unveränderliche Identität. Das `label` ist pflegbar und darf nie verglichen werden. */
  schluessel: string;
  label: string;
  /** Erzwingt den Freitext („Sonstiges") — die Pflicht hängt am Thema, nicht am Label. */
  freitext_pflicht: boolean;
  /**
   * Punkte, die vor der Buchung abgehakt sein müssen. Leere Liste = keine
   * Checkliste (z. B. „Sonstiges"). Hängt am Thema, nicht am Label.
   */
  checkliste: string[];
  /**
   * Decision #238: ISO-Wochentage (1 = Mo … 7 = So), an denen der Pool dieses
   * Themas laut „Buchbare Zeiten" geöffnet hat. null = keine Einschränkung.
   */
  buchbare_tage?: number[] | null;
  /** Callcenter-Horizont des Pools in Tagen (Entscheid Alex 22.10.2026, E1/P3). */
  horizont_tage?: number;
}
export interface VertriebsserviceZustand {
  /** Ist der Dienst benutzbar? */
  verfuegbar: boolean;
  /** Wenn nicht: warum — als fertiger Satz für die Oberfläche. */
  grund: string | null;
  mitglieder: number;
  mit_google: number;
}

/**
 * Live-Status des offenen Zoom-Calls.
 *
 * `besetzt` ist eine LISTE, nicht ein Eintrag: dienstags und donnerstags
 * laufen um 11 Uhr zwei Spuren nebeneinander. Eine leere Liste ist ein
 * gültiger Zustand („gerade niemand im Dienst"), kein Fehler.
 */
export interface VertriebsserviceDienstSlot {
  name: string | null;
  thema: string | null;
  von: string;
  bis: string;
  zoom_url: string;
}

export interface VertriebsserviceDienst {
  /** Serverzeit in Europe/Berlin — die Uhr des Betriebs, nicht die des Browsers. */
  jetzt: string;
  wochentag: number;
  besetzt: VertriebsserviceDienstSlot[];
}

export interface AusarbeitungSlot {
  time: string;
  available_count: number;
}

export interface AusarbeitungBuchung {
  expert_id: number;
  expert_name: string;
  event_id: string | null;
  start: string;
  /** Das gebuchte Thema als Anzeigetext — für die Erfolgsmeldung. */
  thema: string;
}

const dienst = async (): Promise<VertriebsserviceDienst> => {
  try {
    const { data } = await network.get<{ data: VertriebsserviceDienst }>('/vertriebsservice/dienst');
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const themen = async (bereich: VertriebsserviceBereich): Promise<VertriebsserviceThema[]> => {
  try {
    const { data } = await network.get<{ data: VertriebsserviceThema[] }>(
      '/vertriebsservice/themen',
      { params: { bereich } },
    );
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

/**
 * `themaId` optional (§1.14): hat das Thema einen eigenen Pool, zählt dessen
 * Besetzung. Ohne Thema gilt der Pool des Angebots.
 */
const zustand = async (bereich: VertriebsserviceBereich, themaId?: number | null): Promise<VertriebsserviceZustand> => {
  try {
    const { data } = await network.get<{ data: VertriebsserviceZustand }>(
      '/vertriebsservice/zustand',
      { params: { bereich, ...(themaId ? { thema_id: themaId } : {}) } },
    );
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

/**
 * Freie Zeiten eines Tages.
 *
 * RUECKGABE IST DIE HUELLE `{ slots }`, nicht die nackte Liste — dieselbe Form,
 * die `api.callcenter.getAvailability` liefert. Beide Slot-Quellen speisen
 * seit 11.08.2026 denselben Kalender (`CallcenterCalendar`, `slotQuelle`);
 * eine abweichende Form bräuchte dort ein `.then(r => r.slots)` und damit eine
 * zusätzliche Microtask. Begründung ausführlich an `KalenderSlotQuelle`.
 */
const slots = async (
  bereich: VertriebsserviceBereich,
  datum: string,
  /** Optional (§1.14): mit eigenem Pool des Themas kommen dessen Zeiten. */
  themaId?: number | null,
): Promise<{ slots: AusarbeitungSlot[] }> => {
  try {
    const { data } = await network.get<{ data: { slots: AusarbeitungSlot[] } }>(
      '/vertriebsservice/slots',
      { params: { bereich, datum, ...(themaId ? { thema_id: themaId } : {}) } },
    );
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const buchen = async (
  bereich: VertriebsserviceBereich,
  themaId: number,
  start: string,
  freitext: string | undefined,
  /** Bewusst ohne Default — der Server lehnt Themen mit Checkliste ohne Bestätigung ab. */
  checklisteBestaetigt: boolean,
): Promise<AusarbeitungBuchung> => {
  try {
    const { data } = await network.post<{ data: AusarbeitungBuchung }>('/vertriebsservice/buchen', {
      bereich,
      thema_id: themaId,
      start,
      freitext: freitext?.trim() || null,
      checkliste_bestaetigt: checklisteBestaetigt,
    });
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

/**
 * Eine Karte des Portals (§1.12). Texte und Ansprechpartner kommen aus der
 * Pflege — `personen` sind die aktiven Mitglieder des zugeordneten Pools.
 * `schluessel`: `vertrieb` (offener Zoom-Call, nicht buchbar),
 * `ausarbeitung`, `technik`.
 */
export interface VertriebsserviceAngebot {
  schluessel: string;
  buchbar: boolean;
  titel: string;
  kicker: string | null;
  hinweis: string | null;
  personen_label: string | null;
  personen: string[];
  anliegen: string[];
  /** Decision #238: „Buchbare Zeiten" des Pools als Text, null = keine Angabe. */
  zeiten?: string | null;
}

/**
 * Ein Eintrag im Schnellkontakt (§1.13): Buchhaltung, Personalservice,
 * IT-Notfall. Alles pflegbar außer `schluessel` — an dem hängt nur die
 * Akzentfarbe der Kachel.
 */
export interface VertriebsserviceKontakt {
  schluessel: string;
  etikett: string;
  text: string | null;
  email: string | null;
  telefon: string | null;
}

export interface VertriebsserviceAngeboteAntwort {
  /** Anzeige-Auskunft für die Pflege-Schaltfläche — der Schutz sitzt im Backend. */
  darf_pflegen: boolean;
  angebote: VertriebsserviceAngebot[];
  kontakte: VertriebsserviceKontakt[];
}

export interface KontaktEingabe {
  etikett: string;
  text: string | null;
  email: string | null;
  telefon: string | null;
}

/** Ein Angebot in der Pflege-Ansicht — zusätzlich Pool und Dauer. */
export interface PflegeAngebot extends Omit<VertriebsserviceAngebot, 'personen'> {
  meeting_pool_id: number | null;
  sortierung: number;
}

/** Ein Thema in der Pflege-Ansicht — auch inaktive. */
export interface PflegeThema extends VertriebsserviceThema {
  bereich: VertriebsserviceBereich;
  /** Eigener Meeting-Pool (§1.14); `null` = Pool des Angebots. */
  meeting_pool_id: number | null;
  sort_order: number;
  is_active: boolean;
}

export interface PflegePool {
  id: number;
  name: string;
  is_active: boolean;
}

export interface VertriebsservicePflegeAntwort {
  angebote: PflegeAngebot[];
  themen: PflegeThema[];
  kontakte: VertriebsserviceKontakt[];
  pools: PflegePool[];
}

export interface AngebotEingabe {
  titel: string;
  kicker: string | null;
  hinweis: string | null;
  personen_label: string | null;
  anliegen: string[];
  /** Nur bei buchbaren Angeboten — der Server weist ihn beim offenen Zoom-Call ab. Die Dauer kommt aus dem Pool. */
  meeting_pool_id?: number;
}

export interface ThemaEingabe {
  label: string;
  freitext_pflicht: boolean;
  checkliste: string[];
  /** Eigener Meeting-Pool (§1.14); `null` = Pool des Angebots. */
  meeting_pool_id: number | null;
  is_active: boolean;
  sort_order?: number;
}

const angebote = async (): Promise<VertriebsserviceAngeboteAntwort> => {
  try {
    const { data } = await network.get<{ data: VertriebsserviceAngeboteAntwort }>('/vertriebsservice/angebote');
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const pflege = async (): Promise<VertriebsservicePflegeAntwort> => {
  try {
    const { data } = await network.get<{ data: VertriebsservicePflegeAntwort }>('/vertriebsservice/pflege');
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const angebotSpeichern = async (schluessel: string, eingabe: AngebotEingabe): Promise<void> => {
  try {
    await network.put(`/vertriebsservice/pflege/angebote/${schluessel}`, eingabe);
  } catch (error) { throw new ErrorResponse(error); }
};

const kontaktSpeichern = async (schluessel: string, eingabe: KontaktEingabe): Promise<void> => {
  try {
    await network.put(`/vertriebsservice/pflege/kontakte/${schluessel}`, eingabe);
  } catch (error) { throw new ErrorResponse(error); }
};

/** `bereich` nur beim Anlegen — danach ist er fest (die Buchungen hängen daran). */
const themaAnlegen = async (bereich: VertriebsserviceBereich, eingabe: ThemaEingabe): Promise<number> => {
  try {
    const { data } = await network.post<{ data: { id: number } }>('/vertriebsservice/pflege/themen', { bereich, ...eingabe });
    return data.data.id;
  } catch (error) { throw new ErrorResponse(error); }
};

const themaSpeichern = async (id: number, eingabe: ThemaEingabe): Promise<void> => {
  try {
    await network.put(`/vertriebsservice/pflege/themen/${id}`, eingabe);
  } catch (error) { throw new ErrorResponse(error); }
};

const vertriebsserviceApi = {
  dienst,
  themen,
  zustand,
  slots,
  buchen,
  angebote,
  pflege,
  angebotSpeichern,
  kontaktSpeichern,
  themaAnlegen,
  themaSpeichern,
};

export default vertriebsserviceApi;
